import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

import { ProjectDialogComponent } from '../project-dialog/project-dialog.component';
import { ProjectsService } from '../../services/projects.service';
import { AuthService } from '../../services/auth.service';
import { LanguageService } from '../../services/language.service';
import * as Projects from '../../shared/projects.model';
import { PartialProject, ReclaimVotesModalData, VoteProcessData } from '../../shared/projects.model';
import { TagsService } from 'src/app/services/tags.service';
import { Router } from '@angular/router';
import { ModalService } from '../../shared/services/modal/modal.service';
import { ModalConstants } from '../../shared/services/modal/modal.constants';
import { ProjectsConstants } from '../projects.constants';
import { VoteCompleteDialogComponent } from '../vote-complete-dialog/vote-complete-dialog.component';
import { VoteConfirmationDialogComponent } from '../vote-confirmation-dialog/vote-confirmation-dialog.component';
import { VoteDialogComponent } from '../vote-dialog/vote-dialog.component';
import { ReclaimVoteDialogComponent } from '../reclaim-vote-dialog/reclaim-vote-dialog.component';
import { Constants } from '../../app.constants';
import { User } from '../../shared/user.model';
import {
  ProposeInitiativeModalComponent
} from '../../shared/modals/propose-initiative-modal/propose-initiative-modal.component';
import { FilterTag, TagMetaData } from '../../volunteering-module/models/volunteering.model';
import { SVG } from '../../shared/svg.constants';
import { isMobile } from '../../utils/screen.utils';
import { NsSimpleInfoModalComponent } from '../../shared/modals/ns-simple-info-modal/ns-simple-info-modal.component';
import { RouterUrls } from '../../shared/router-urls';
import { ModalQueueService } from '../../shared/services/modal/modal-queue.service';

@Component({
  selector: 'app-browse-projects',
  templateUrl: './browse-projects.component.html',
  styleUrls: ['./browse-projects.component.scss']
})
export class BrowseProjectsComponent implements OnInit, OnDestroy {
  readonly Constants = Constants;
  readonly ProjectConstants = ProjectsConstants;
  readonly SVG = SVG;
  private projectLinkHtml(project: PartialProject): string {
    return `<a class="ns-dark-blue-link" href="${this.currentLanguage + '/' + RouterUrls.BrowseProjects + '/' + project.slug}">${project.name[this.currentLanguage]}</a>`;
  }

  paginationLimit = 9;
  projects: Projects.PartialProject[];
  filteredProjects: Projects.PartialProject[] = [];
  filteredProjectsExcludingNorthStar: Projects.PartialProject[] = [];
  filteredProjectsExcludingRegions: Projects.PartialProject[] = [];
  filteredProjectsExcludingTopics: Projects.PartialProject[] = [];
  displayedProjects: Projects.PartialProject[] = [];
  ownMoneyProjects: Projects.PartialProject[];
  user: User;
  username: string;
  isLogged = false;
  isPledge = false;
  hasSubscription = false;
  ownMoneyDataReady = false;
  subscriptions: Subscription = new Subscription();
  clickEventSubscription: Subscription;

  // North Star slider variables
  @ViewChild('scrollableDiv') scrollableDiv: ElementRef;
  categoriesSliderWidth = 1132;
  buttonContainerWidth = 125;
  buttonContainerMargins = 20;
  arrowButtons = 32;
  hasHorizontalScroll = false;
  selectedNorthStar: string = ProjectsConstants.CAT_ALL;
  selectedVettingType: string = undefined;
  scrollInterval;
  isCategoriesSliderInitialized = false;
  northStarProjectCounts: { [category: string]: number } = {};

  //Votes
  votesAvailable = 0;

  // Filters and sorting variables
  showFilters = false;
  vettedByUsCount = 0;
  vettedByEmployeesCount = 0;
  regionTags: FilterTag[] = [];
  topicTags: FilterTag[] = [];
  selectedRegionTags: TagMetaData[] = [];
  selectedTopicTags: TagMetaData[] = [];
  regionTagsInfo: TagMetaData[] = [];
  filteredRegionTagsInfo: TagMetaData[] = [];
  topicsTagsInfo: TagMetaData[] = [];
  isSortContextMenuOpen = false;
  selectedSortingOption: any = ProjectsConstants.SORT_BY_RELEVANT;
  isRegionFilterFranceOnly = false;
  displayVoteCounter = false;
  isMobile = false;
  screenWidth: number;
  // Caches the presence of each North Star in projects to avoid repetitive loops during change detection cycle
  northStarInFilterMap: {[key: string]: boolean} = {};
  // Stores sanitized SVGs to avoid doing the calculation in every change detection cycle
  private svgCache = new Map<string, SafeHtml>();

  constructor(private modal: ModalService,
              private modalQueue: ModalQueueService,
              private projectsService: ProjectsService,
              private authService: AuthService,
              private titleService: Title,
              private router: Router,
              private languageService: LanguageService,
              private meta: Meta,
              private tagsService: TagsService,
              private translate: TranslateService,
              private sanitizer: DomSanitizer) {
    this.clickEventSubscription = this.tagsService.getClickEventBrowseProjects().subscribe(() => {
      this.updateTags();
    });
  }

  get currentLanguage() {
    return this.languageService.getLanguage();
  }

  @HostListener('document:mousedown', ['$event'])
  handleOutsideClick(event) {
    if (
      this.isSortContextMenuOpen &&
      !event.target.classList.contains('sort-button') &&
      !event.target.classList.contains('context-menu') &&
      !event.target.classList.contains('radio-option')
    ) {
      this.isSortContextMenuOpen = false;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.updateNorthStarsSliderState();
  }

  ngOnInit(): void {
    this.isMobile = isMobile();
    this.updateTags();
    this.authService.getCurrentUser().subscribe({
      next: (user: User) => {
        this.user = user;
        this.username = user.name;
        this.isPledge = user.pledge_id !== null;
        this.paginationLimit = this.isPledge ? 8 : 9;
        this.hasSubscription = user.has_sub;
        this.getVotedProjects();
        this.updateDisplayedProjects();
        this.isLogged = true;
        this.isLogged ? this.getOwnMoneyData() : this.ownMoneyDataReady = true;
      }, error: () => {
        this.updateDisplayedProjects();
        this.ownMoneyDataReady = true;
      }
    });
    this.populateProjectsList();
  }


  populateProjectsList() {
    this.projectsService.getProjects().pipe(take(1)).subscribe({
      next: (projects) => {
        this.projects = projects;
        // Reset counts
        this.northStarProjectCounts = {};
        // SET UP FILTERS
        // Deep clone of projects list
        this.filteredProjects = JSON.parse(JSON.stringify(this.projects));
        this.filteredProjectsExcludingNorthStar = JSON.parse(JSON.stringify(this.projects));
        // Object to keep track of unique tags (More efficient)
        const uniqueRegionTags: {[key: string]: number} = {};
        const uniqueTopicTags: {[key: string]: number} = {};
        // We list all the available tags for filtering
        this.projects?.forEach(
          (project) => {
            // We check if there is any project with each north star category, to display them or not
            const categories = [project.north_star, project.north_star_2, project.north_star_3];
            categories.forEach(category => {
              if (category) {
                if (!this.northStarProjectCounts[category]) {
                  this.northStarProjectCounts[category] = 1;
                } else {
                  this.northStarProjectCounts[category]++;
                }
              }
            });
            // Count item number of vetted by type tags
            project.vetted_by === ProjectsConstants.VETTED_BY_NOOS ?
              this.vettedByUsCount++ :
              this.vettedByEmployeesCount++;

            // Fill available tags array from project list
            project.tags?.forEach((tag: FilterTag) => {
              const tagKey = JSON.stringify(tag); // Convert the object to a string to use as a key

              if (tag.type === ProjectsConstants.TAG_TYPE_REGION) {
                // If the tag is not registered, we save it with a count of 1
                if (!uniqueRegionTags[tagKey]) {
                  uniqueRegionTags[tagKey] = 1;
                  this.regionTags.push(tag);
                  this.regionTagsInfo.push({tag: tag, itemsNumber: 1});
                }
                // Else, we increase the counter of items
                else {
                  uniqueRegionTags[tagKey]++;
                  const tagInfo = this.regionTagsInfo.find(ti => (ti.tag.name === tag.name));
                  if (tagInfo) {
                    tagInfo.itemsNumber++;
                  }
                }
              }

              if (tag.type === ProjectsConstants.TAG_TYPE_TOPIC) {
                // If the tag is not registered, we save it with a count of 1
                if (!uniqueTopicTags[tagKey]) {
                  uniqueTopicTags[tagKey] = 1;
                  this.topicTags.push(tag);
                  this.topicsTagsInfo.push({tag: tag, itemsNumber: 1});
                }
                // Else, we increase the counter of items
                else {
                  uniqueTopicTags[tagKey]++;
                  const tagInfo = this.topicsTagsInfo.find(ti => (ti.tag.name === tag.name));
                  if (tagInfo) {
                    tagInfo.itemsNumber++;
                  }
                }
              }
            });
          });
        this.filteredRegionTagsInfo = JSON.parse(JSON.stringify(this.regionTagsInfo));
        this.filterRegionTags(ProjectsConstants.REGION_FR_FILTER_KEY, false);
        this.updateDisplayedProjects();
        this.updateNorthStarInFilterMap();
        setTimeout(
          () => {
            if (!this.isCategoriesSliderInitialized) {
              this.updateNorthStarsSliderState();
              this.isCategoriesSliderInitialized = true;
            }
          },
          300
        );
      }
    });
  }

  getVotedProjects() {
    this.projectsService.getSupportedProjects().subscribe({
      next: (projects) => {
        this.votesAvailable = 5 - projects.length;
        this.displayVoteCounter = this.isPledge;
      }
    });
  }

  onOpenCloseSortContextMenu() {
    this.isSortContextMenuOpen = !this.isSortContextMenuOpen;
  }

  filterByNorthStar(northStar: string) {
    if (northStar === ProjectsConstants.CAT_ALL || this.projectWithNorthStarInFilter(northStar)) {
      this.selectedNorthStar = northStar;
      this.filterProjects();
    }
  }

  filterByVettedType(option: string) {
    // If disabled we do nothing
    if (option === ProjectsConstants.VETTED_BY_EMPLOYEE && this.vettedByEmployeesCount === 0) return;
    if (option === ProjectsConstants.VETTED_BY_NOOS && this.vettedByUsCount === 0) return;

    // If already selected, we unselect it
    if (option === this.selectedVettingType) {
      this.selectedVettingType = undefined;
    } else {
      this.selectedVettingType = option;

      // If no projects with the selected vetting type, we do nothing, is disabled
      const isVettedOptionValid =
        (option === ProjectsConstants.VETTED_BY_NOOS && this.vettedByUsCount > 0) ||
        (option === ProjectsConstants.VETTED_BY_EMPLOYEE && this.vettedByEmployeesCount > 0);

      if (!isVettedOptionValid) {
        return;
      }
    }
    // We filter again with the selected vetting option
    this.filterProjects();
  }

  filterByTag(ti: TagMetaData) {
    // If disabled we do nothing
    if (ti.itemsNumber === 0 && ((ti.tag.type === ProjectsConstants.TAG_TYPE_REGION && !this.isRegionFilterAvailable(ti))
      || (ti.tag.type === ProjectsConstants.TAG_TYPE_TOPIC && !this.isTopicFilterAvailable(ti)))) {
      return;
    }

    if (ti.tag.type === ProjectsConstants.TAG_TYPE_REGION) {
      if (this.selectedRegionTags.some(t => (ti.tag.name === t.tag.name))) {
        this.selectedRegionTags = this.selectedRegionTags.filter(t => !(ti.tag.name === t.tag.name));
      } else {
        this.selectedRegionTags.push(ti);
      }
    } else if (ti.tag.type === ProjectsConstants.TAG_TYPE_TOPIC) {
      if (this.selectedTopicTags.some(t => (ti.tag.name === t.tag.name))) {
        this.selectedTopicTags = this.selectedTopicTags.filter(t => !(ti.tag.name === t.tag.name));
      } else {
        this.selectedTopicTags.push(ti);
      }
    }
    this.filterProjects();
  }

  filterProjects() {
    // Removes the tags that became disabled because other rules but stayed inside the selected tags array.
    this.selectedTopicTags = this.selectedTopicTags.filter(ti => this.isTopicFilterAvailable(ti) || ti.itemsNumber > 0);
    this.selectedRegionTags = this.selectedRegionTags.filter(ti => this.isRegionFilterAvailable(ti) || ti.itemsNumber > 0);

    this.filteredProjects = this.filterProjectsExcluding();

    // Update the tag counts based on the filtered projects
    this.updateTagCounts(this.filteredProjects);
    // Count item number of vetted by type tags
    this.vettedByUsCount = 0;
    this.vettedByEmployeesCount = 0;
    this.filteredProjects.forEach(
      (project) => {
        project.vetted_by === ProjectsConstants.VETTED_BY_NOOS ?
          this.vettedByUsCount++ :
          this.vettedByEmployeesCount++;
      }
    )
    // This filter is needed to avoid them filtering over themselves
    this.filteredProjectsExcludingNorthStar = this.filterProjectsExcluding(ProjectsConstants.FILTER_KEY_NORTH_STAR);
    this.filteredProjectsExcludingRegions = this.filterProjectsExcluding(ProjectsConstants.FILTER_KEY_REGION);
    this.filteredProjectsExcludingTopics = this.filterProjectsExcluding(ProjectsConstants.FILTER_KEY_TOPIC);
    this.updateDisplayedProjects();
    this.updateNorthStarInFilterMap();
  }

  filterProjectsExcluding(excludeFilter?: string): PartialProject[] {
    return this.projects.filter(
      (project) => {
        let passNorthStarFilter = false;
        let passVettingFilter = false;
        let passRegionFilter = false;
        let passTopicFilter = false;

        // BY NORTH STAR
        const projectNorthStars = [project.north_star, project.north_star_2, project.north_star_3];
        passNorthStarFilter = excludeFilter === ProjectsConstants.FILTER_KEY_NORTH_STAR
          || this.selectedNorthStar === ProjectsConstants.CAT_ALL
          || projectNorthStars.includes(this.selectedNorthStar);

        // BY VETTING TYPE
        passVettingFilter =
          this.selectedVettingType === undefined ||
          (this.selectedVettingType === ProjectsConstants.VETTED_BY_NOOS && project.vetted_by === ProjectsConstants.VETTED_BY_NOOS) ||
          (this.selectedVettingType !== ProjectsConstants.VETTED_BY_NOOS && project.vetted_by !== ProjectsConstants.VETTED_BY_NOOS);

        // BY Region TYPE
        passRegionFilter = excludeFilter === ProjectsConstants.FILTER_KEY_REGION
          || this.selectedRegionTags.length === 0
          || this.selectedRegionTags.some(t => project.tags.some(tag => (tag.name === t.tag.name)));

        // BY Topic TYPE
        passTopicFilter = excludeFilter === ProjectsConstants.FILTER_KEY_TOPIC
          || this.selectedTopicTags.length === 0
          || this.selectedTopicTags.some(t => project.tags.some(tag => (tag.name === t.tag.name)));

        return passNorthStarFilter && passVettingFilter && passRegionFilter && passTopicFilter;
      });
  }

  isRegionTagSelected(tag) {
    return this.selectedRegionTags.some(t => (tag.name === t.tag.name));
  }

  isTopicTagSelected(tag) {
    return this.selectedTopicTags.some(t => (tag.name === t.tag.name));
  }

  clearVettingTypeFilter() {
    this.selectedVettingType = undefined;
    this.filterProjects()
  }

  clearRegionFilter() {
    this.selectedRegionTags = [];
    this.filterProjects();
  }

  clearTopicFilter() {
    this.selectedTopicTags = [];
    this.filterProjects();
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  setSorting(option: string) {
    this.selectedSortingOption = option;
    this.sortBy();
    this.updateDisplayedProjects();
  }

  sortBy() {
    switch (this.selectedSortingOption) {
      case ProjectsConstants.SORT_BY_RELEVANT:
        this.filteredProjects = JSON.parse(JSON.stringify(this.projects));
        this.filterProjects();
        break;
      case ProjectsConstants.SORT_BY_DATE:
        this.filteredProjects.sort((a: PartialProject, b: PartialProject) =>
          // Sort by creation_date in descending order, placing projects with null creation_date at the bottom.
          (a.creation_date === null) ? 1 : (b.creation_date === null) ? -1 : b.creation_date.localeCompare(a.creation_date)
        );
        break;
      case ProjectsConstants.SORT_BY_VOTES:
        this.filteredProjects.sort((a: PartialProject, b: PartialProject) => {
          return b.num_votes - a.num_votes;
        });
        break;
    }
  }

  openProjectDialog(project: PartialProject) {
    const dialogRef = this.modal.openFixedModal(project.id, ProjectDialogComponent, ModalConstants.PROJECT_PANEL_CLASS);
    dialogRef.componentInstance.navigateToProjectOnVote = false;
    this.subscriptions.add(
      dialogRef.afterClosed().subscribe(
        (code: string) => {
          const voteData: VoteProcessData = {
            hasSubs: this.hasSubscription,
            isPledge: this.isPledge,
            projectId: project.id,
            project
          };

          if (code) {
            this.handleVoteProcess(code, voteData);
          }
        }
      ));
  }

  isVoted(projectId: number): boolean {
    return this.projectsService.isVoted(projectId);
  }

  getOwnMoneyData(): void {
    this.projectsService.getProjectsOwnMoney().pipe(take(1)).subscribe({
      next: (projects: PartialProject[]) => {
        this.ownMoneyDataReady = true;
        if (projects) {
          this.ownMoneyProjects = projects;
        }
      }, error: () => {
        this.ownMoneyDataReady = true;
      }
    })
  }

  showMore() {
    this.paginationLimit += 6;
    this.updateDisplayedProjects();
  }

  updateTags() {
    this.titleService.setTitle(this.translate.instant('BROWSE_PAGE.meta_title_browsepage'));
    this.meta.updateTag({
      name: 'description',
      content: this.translate.instant('BROWSE_PAGE.meta_description_browsepage')
    });
    this.meta.updateTag({property: 'og:title', content: this.translate.instant('BROWSE_PAGE.meta_title_browsepage')});
    this.meta.updateTag({
      property: 'og:description',
      content: this.translate.instant('BROWSE_PAGE.meta_description_browsepage')
    });
    this.meta.updateTag({property: 'og:type', content: 'URL'});
    this.meta.updateTag({property: 'og:url', content: window.location.href});
    this.meta.updateTag({
      property: 'og:image',
      content: Constants.APP_URL + 'assets/landing_videos_images/noos-north_star.png'
    });
  }

  openProposeProjectModal() {
    const panelClass = isMobile() ? ModalConstants.NO_PADDING_FULLSCREEN_PANEL_CLASS : ModalConstants.DEFAULT_PANEL_CLASS;
    const dialogWidth = isMobile() ? window.innerWidth : 600;
    const dialogHeight = isMobile() ? window.innerHeight : 460;
    const maxHeight = window.innerHeight > 716 ? 716 : window.innerHeight;

    const dialogRef = this.modal.openAdaptableModal(null, ProposeInitiativeModalComponent, panelClass, dialogWidth, dialogHeight, 600, maxHeight);
    dialogRef.componentInstance.user = this.user;
    dialogRef.componentInstance.initiativeType = Constants.INITIATIVE_TYPE_NGO_PROJECT;
    dialogRef.componentInstance.title = this.translate.instant('INITIATIVE_PROPOSAL_MODAL.add_project');
    dialogRef.componentInstance.explanation1 = this.translate.instant('INITIATIVE_PROPOSAL_MODAL.project_explanation_1');
    dialogRef.componentInstance.explanation2 = this.translate.instant('INITIATIVE_PROPOSAL_MODAL.project_explanation_2', {company: this.user?.company_name});
    dialogRef.componentInstance.explanation3 = this.translate.instant('INITIATIVE_PROPOSAL_MODAL.project_suggestion_text');
  }

  updateTagCounts(filteredProjects: any[]) {
    // Reset the itemsNumber for all tags to 0
    this.filteredRegionTagsInfo.forEach(tagInfo => tagInfo.itemsNumber = 0);
    this.topicsTagsInfo.forEach(tagInfo => tagInfo.itemsNumber = 0);

    // Loop through each filtered project to update the itemsNumber
    filteredProjects.forEach(project => {
      project.tags?.forEach((tag: FilterTag) => {
        if (tag.type === ProjectsConstants.TAG_TYPE_REGION) {
          // Find the corresponding tag in regionTagsInfo and update its itemsNumber
          const tagInfo = this.regionTagsInfo.find(ti => (ti.tag.name === tag.name));
          if (tagInfo) {
            tagInfo.itemsNumber++;
          }
        }

        if (tag.type === ProjectsConstants.TAG_TYPE_TOPIC) {
          // Find the corresponding tag in topicTagsInfo and update its itemsNumber
          const tagInfo = this.topicsTagsInfo.find(ti => (ti.tag.name === tag.name));
          if (tagInfo) {
            tagInfo.itemsNumber++;
          }
        }
      });
    });
  }

  startScrolling(direction: 'left' | 'right') {
    const el = this.scrollableDiv?.nativeElement;
    const scrollAmount = 10;
    // Ensure any existing interval is cleared
    this.stopScrolling();

    this.scrollInterval = setInterval(() => {
      el.scrollLeft += (direction === 'left' ? -scrollAmount : scrollAmount);
    }, 50); // Adjust this value for faster or slower interval
  }

  updateNorthStarsSliderState() {
    this.screenWidth = window.innerWidth;
    if (!isMobile()) {
      const el = this.scrollableDiv?.nativeElement;
      this.hasHorizontalScroll = el?.scrollWidth > el?.clientWidth;
      this.categoriesSliderWidth = this.screenWidth -
        this.buttonContainerWidth - (this.buttonContainerMargins * 2) - (this.hasHorizontalScroll ? (this.arrowButtons * 2) : 0);
    }
  }

  stopScrolling() {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }

  projectWithNorthStarInFilter(northStar: string): boolean {
    return this.northStarInFilterMap[northStar];
  }

  isRegionFilterAvailable(ti: TagMetaData): boolean {
    return this.filteredProjectsExcludingRegions.length === 0 || this.filteredProjectsExcludingRegions.some(project =>
      project.tags?.some(stag => (ti.tag.name === stag.name)));
  }

  isTopicFilterAvailable(ti: TagMetaData): boolean {
    return this.filteredProjectsExcludingTopics.length === 0 || this.filteredProjectsExcludingTopics.some(project =>
      project.tags?.some(stag => (ti.tag.name === stag.name)));
  }


  svgSanitizer(svg: string): SafeHtml {
    if (!this.svgCache.has(svg)) {
      const sanitized = this.sanitizer.bypassSecurityTrustHtml(svg);
      this.svgCache.set(svg, sanitized);
    }
    return this.svgCache.get(svg);
  }

  updateDisplayedProjects() {
    this.displayedProjects = this.filteredProjects.slice(0, this.paginationLimit);
  }

  setRegionFilterFranceOnly(setActive: boolean) {
    if (setActive) {
      this.isRegionFilterFranceOnly = true;
      this.filterRegionTags(ProjectsConstants.REGION_FR_FILTER_KEY, true);
    } else {
      this.isRegionFilterFranceOnly = false;
      this.filterRegionTags(ProjectsConstants.REGION_FR_FILTER_KEY, false);
    }
  }

  filterRegionTags(regionFilterKey: string, isInclusiveFilter: boolean) {
    this.selectedRegionTags = [];
    // If 'isInclusiveFilter' is true, it keeps tags that start with 'regionFilterKey', else it removes them.
    this.filteredRegionTagsInfo = JSON.parse(JSON.stringify(this.regionTagsInfo));
    this.filteredRegionTagsInfo = this.filteredRegionTagsInfo.filter(tagInfo =>
      isInclusiveFilter === tagInfo.tag.name.startsWith(regionFilterKey));

    this.filterProjects();
  }

  updateNorthStarInFilterMap() {
    this.northStarInFilterMap = {};
    this.filteredProjectsExcludingNorthStar.forEach(project => {
      [project.north_star, project.north_star_2, project.north_star_3].forEach(northStar => {
        if (northStar) {
          this.northStarInFilterMap[northStar] = true;
        }
      });
    });
  }

  manageVotes() {
    const modalHeight = isMobile() ? window.innerHeight : Math.min(window.innerHeight, 734);
    const modalWidth = isMobile() ? window.innerWidth : 700;

    const manageVotesModalRef = this.modal.openFixedModal(
      {}, ReclaimVoteDialogComponent, isMobile() ? ModalConstants.NO_PADDING_FULLSCREEN_PANEL_CLASS
        : ModalConstants.NO_PADDING_PANEL_CLASS, modalWidth, modalHeight);

    manageVotesModalRef.componentInstance.isManagingVotes = true;
    manageVotesModalRef.afterClosed().subscribe((data: ReclaimVotesModalData) => {
      if (data.code === ProjectsConstants.MANAGED_VOTES) {
        this.getVotedProjects();
        const minModalHeight = 350;
        const minModalWidth = !isMobile() ? 600 : 300;
        const maxModalWidth = !isMobile() ? 600 : (window.innerWidth - 40);
        const infoModalRef = this.modal.openAdaptableModal(
          {}, NsSimpleInfoModalComponent, this.isMobile ? ModalConstants.PADDING_16_PANEL_CLASS : ModalConstants.DEFAULT_PANEL_CLASS, minModalWidth, minModalHeight, maxModalWidth);
        const modal = infoModalRef.componentInstance;
        modal.title = this.translate.instant('RECLAIM.votes_reclaimed_successfully', {plural: data.projects.length > 1 ? 's' : ''});
        const key = data.projects.length > 1 ? 'RECLAIM.votes_reclaimed_text_plural' : 'RECLAIM.votes_reclaimed_text';
        const params = data.projects.length > 1 ?
          {
            projectsMinusLast: this.joinProjectNamesExceptLast(data.projects),
            lastProject: this.projectLinkHtml(data.projects[data.projects.length - 1])
          } :
          {project: this.projectLinkHtml(data.projects[0])};
        modal.infoText = this.translate.instant(key, params);
        if (data.projects.length > 1) {
          modal.infoText += this.translate.instant('RECLAIM.new_projects_support', {number: data.projects.length});
        }
        modal.infoTextDesktopClasses = 'ns-font-16 ns-mt-48 ns-mb-48';
        modal.buttonText = this.translate.instant('RECLAIM.okay');
        modal.buttonType = Constants.BUTTON_TYPE_DEFAULT_GREEN_LINE_B;
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  joinProjectNamesExceptLast(projects: PartialProject[]): string {
    if (!projects || projects.length === 0) {
      return '';
    }

    return projects.slice(0, -1).map(project => this.projectLinkHtml(project)).join(', ');
  }

  private handleVoteProcess(projectModalData: any, voteData: VoteProcessData) {

    switch (projectModalData) {
      case ProjectsConstants.VOTE_CONFIRMATION:
        this.modalQueue.addModalToQueue({
          id: this.modalQueue.ids.VOTE_CONFIRMATION_ID,
          layer: 8,
          priority: 100,
          openModalFn: () => {
            const modalRef = this.modal.openFixedModal(voteData, VoteConfirmationDialogComponent, ModalConstants.VOTE_PANEL_CLASS, 737);
            this.votesAvailable--;
            modalRef.afterClosed().subscribe(() => {
              this.modalQueue.closeModal(this.modalQueue.ids.VOTE_CONFIRMATION_ID);
            })
          }
        });
        break;
      case ProjectsConstants.FIVE_VOTES_COMPLETED:
        this.modal.openFixedModal(voteData, VoteCompleteDialogComponent, ModalConstants.VOTE_PANEL_CLASS, 737);
        this.votesAvailable = 0;
        break;
      case ProjectsConstants.RECLAIM_VOTE: {
        voteData.stopSupporting = false;
        const voteModalRef = this.modal.openFixedModal(voteData, VoteDialogComponent, ModalConstants.VOTE_PANEL_CLASS, 737);

        this.subscriptions.add(
          voteModalRef.afterClosed().subscribe(
            voteModalData => {
              if (voteModalData && voteModalData === ProjectsConstants.RECLAIM_VOTE) {
                const modalHeight = isMobile() ? window.innerHeight : Math.min(window.innerHeight, 754);
                const modalWidth = isMobile() ? window.innerWidth : 700;
                const reclaimVoteModalRef = this.modal.openFixedModal(
                  voteData, ReclaimVoteDialogComponent, isMobile() ? ModalConstants.NO_PADDING_FULLSCREEN_PANEL_CLASS
                    : ModalConstants.NO_PADDING_PANEL_CLASS, modalWidth, modalHeight);

                reclaimVoteModalRef.afterClosed().subscribe((data: ReclaimVotesModalData) => {
                  if (data.code === ProjectsConstants.RECLAIMED_VOTE) {
                    this.votesAvailable = 0;
                    this.modal.openFixedModal(voteData, VoteCompleteDialogComponent, ModalConstants.VOTE_PANEL_CLASS, 737);
                  }
                });
              }
            }
          ));
        break;
      }
      default:
        if (typeof projectModalData === 'number') {
          const otherProject = this.projects.find(project => project.id === projectModalData);
          if (otherProject) {
            this.openProjectDialog(otherProject);
          }
        }
        break;
    }

    // Update votes available on vote completed
    this.projectsService.supportedProjects$.pipe(take(1)).subscribe({
      next: (projects: PartialProject[]) => {
        this.votesAvailable = 5 - projects.length;
      }
    });
  }
}
